import {linkEmplTickets} from "app/employee/core/employeeLinks";
import type {BootstrapResult} from "app/employee/state/bootstrapEmployee/mutations";
import {Trans, useTranslation} from "react-i18next";
import {IonBadge, IonIcon, IonItem, IonLabel, IonList, IonMenuToggle, isPlatform} from "@ionic/react";
import {
  bookOutline,
  chatbubblesOutline,
  documentTextOutline,
  fileTrayFullOutline,
  helpCircleOutline,
  logoGooglePlaystore,
  notificationsOutline,
  readerOutline
} from "ionicons/icons";
import {useRouteMatch} from "react-router-dom";
import {useEnabledFeatures} from "app/employee/state/bootstrapEmployee/selectors";
import {Feature} from "app/gql/graphqlSchema";
import {styled} from "styled-components";
import {NotificationCountBadge} from "app/employee/menu/NotificationCountBadge";
import {AnimatedIonIcon} from "app/employee/menu/AnimatedIonIconBadge";
import {useNotificationCount} from "app/employee/notification/useNotificationCount";
import {NotificationPoll} from "app/employee/notification/NotificationPoll";
import type {IonItemProps} from "app/utils/types";
import {NativeNotificationsHandler} from "app/employee/nativeApp/NativeNotificationsHandler";
import {nativeAppAdapter} from "app/employee/nativeApp/FSAcademyAndroidAdapter";

interface NavIonItemProps extends IonItemProps {
  exact?: boolean;
  $nested?: boolean;
  parentPath?: string;
}

const StyledIonItem = styled(IonItem)<Pick<NavIonItemProps, "$nested">>`
  ${({$nested}) =>
    $nested &&
    `
    --padding-start: 32px;  
  `}
`;

const NavIonItem = (props: NavIonItemProps) => {
  const {parentPath, routerLink, $nested, exact} = props;
  const path = routerLink ?? "";
  const match = useRouteMatch({path, exact: exact ?? false});
  const partialMatch = useRouteMatch({path: parentPath ?? path, exact: false});
  if ($nested && !partialMatch) {
    return null;
  }
  return <StyledIonItem {...props} color={match ? "primary" : undefined} />;
};

const DisabledText = styled.p`
  &&& {
    font-size: 0.7rem;
    white-space: normal;
    line-height: 1rem;
    font-weight: bold;
  }
`;

const DisabledIonLabel = ({feature}: {feature: Feature}) => {
  const checkFeature = useEnabledFeatures();
  const {t} = useTranslation();

  return checkFeature(feature) ? null : (
    <DisabledText>{t("g.feature-disabled", {defaultValue: "Тази услуга не е активна за Вашата фирма"})}</DisabledText>
  );
};

export const LoggedInMenuItems = ({bootstrapResult}: {bootstrapResult?: BootstrapResult}) => {
  const {t} = useTranslation();
  const checkFeature = useEnabledFeatures();
  const {unreadCount} = useNotificationCount();

  if (bootstrapResult?.__typename !== "BootstrapEmployee") {
    return null;
  }

  const employeeName = bootstrapResult.employee.name;
  const employeeClientName = bootstrapResult.employee.client.name;

  const mobileAndroidBrowser = isPlatform("android") && !nativeAppAdapter.isRunningInAndroidApp();

  return (
    <>
      <IonList>
        <IonItem>
          <IonLabel class="ion-text-wrap">
            <strong>{employeeName}</strong>
            <p>{employeeClientName}</p>
          </IonLabel>
        </IonItem>
        <IonMenuToggle>
          <NavIonItem
            disabled={!checkFeature(Feature.Course)}
            routerDirection="root"
            routerLink="/employees/course/current"
          >
            <IonIcon icon={bookOutline} slot="start" />
            <IonLabel>
              <Trans i18nKey="menu.course" t={t} />
              <DisabledIonLabel feature={Feature.Course} />
            </IonLabel>
          </NavIonItem>
          <NavIonItem
            disabled={!checkFeature(Feature.IncomingControl)}
            routerDirection="root"
            routerLink="/employees/inc-doc"
          >
            <IonIcon icon={fileTrayFullOutline} slot="start" />
            <IonLabel>
              <Trans i18nKey="menu.incoming-control" t={t} />
              <DisabledIonLabel feature={Feature.IncomingControl} />
            </IonLabel>
          </NavIonItem>

          <NavIonItem
            disabled={!checkFeature(Feature.Organizer)}
            routerDirection="root"
            routerLink="/employees/organizer"
          >
            <IonIcon icon={documentTextOutline} slot="start" />
            <IonLabel>
              <Trans i18nKey="menu.organizer" t={t} />
              <DisabledIonLabel feature={Feature.Organizer} />
            </IonLabel>
          </NavIonItem>
          <NavIonItem
            disabled={!checkFeature(Feature.ControlCard)}
            routerDirection="root"
            routerLink="/employees/control-card"
          >
            <IonIcon icon={readerOutline} slot="start" />
            <IonLabel>
              <Trans i18nKey="menu.control-card" t={t} />
              <DisabledIonLabel feature={Feature.ControlCard} />
            </IonLabel>
          </NavIonItem>
          {checkFeature(Feature.Ticket) && (
            <NavIonItem routerDirection="root" routerLink={linkEmplTickets()}>
              <IonIcon icon={chatbubblesOutline} slot="start" />
              <IonLabel>
                <Trans i18nKey="menu.tickets" t={t} />
              </IonLabel>
            </NavIonItem>
          )}
          <NavIonItem routerDirection="root" routerLink="/employees/notifications">
            <AnimatedIonIcon $animated={unreadCount > 0} icon={notificationsOutline} slot="start" />
            <IonLabel>
              <Trans defaultValue="Известия" i18nKey="menu.notifications" t={t} /> <NotificationCountBadge />
            </IonLabel>
          </NavIonItem>
          <NavIonItem routerDirection="root" routerLink="/employees/help">
            <IonIcon icon={helpCircleOutline} slot="start" />
            <IonLabel>
              <Trans defaultValue="Помощ" i18nKey="menu.help" t={t} />
            </IonLabel>
          </NavIonItem>
          {mobileAndroidBrowser && (
            <NavIonItem routerDirection="root" routerLink="/employees/android-app">
              <IonIcon icon={logoGooglePlaystore} slot="start" />
              <IonLabel>
                <IonBadge>
                  <Trans i18nKey="menu.new-badge" t={t} />
                </IonBadge>{" "}
                <Trans i18nKey="menu.android-app" t={t} />
              </IonLabel>
            </NavIonItem>
          )}
        </IonMenuToggle>
      </IonList>
      <NotificationPoll />
      <NativeNotificationsHandler />
    </>
  );
};
